import React, { useMemo, useState } from "react";
import { api, extractError } from "../lib/api";
import { useAuth, hasPermission } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";

const BUYER_CREATE_KINDS = [
  { value: "BUYER", label: "Buyer" },
  { value: "DEALER", label: "Dealer" },
];

export default function PartyCombobox({
  parties = [],
  partyId = "",
  partyName = "",
  onChange,
  onPartyCreated,
  createKind = "BUYER",
  placeholder = "Search or select…",
  testIdPrefix = "party-combobox",
}) {
  const { user } = useAuth();
  const canCreate = hasPermission(user, "contracts.create") || hasPermission(user, "parties.manage");
  const [comboOpen, setComboOpen] = useState(false);
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newKind, setNewKind] = useState(createKind === "BROKER" ? "BROKER" : "BUYER");

  const selectedLabel = useMemo(() => {
    if (partyId) {
      const p = parties.find((x) => String(x.id) === String(partyId));
      return p?.display_name || partyName || "";
    }
    return partyName || "";
  }, [partyId, partyName, parties]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return parties;
    return parties.filter((p) => String(p.display_name || "").toLowerCase().includes(needle));
  }, [parties, q]);

  const openCreate = () => {
    setNewName(q.trim());
    setNewKind(
      createKind === "BROKER"
        ? "BROKER"
        : createKind === "SUPPLIER"
          ? "SUPPLIER"
          : createKind === "FARMER"
            ? "FARMER"
            : "BUYER",
    );
    setCreateOpen(true);
  };

  const createParty = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    try {
      setCreating(true);
      const r = await api.post("/parties", { kind: newKind, display_name: name });
      const row = r.data;
      onPartyCreated?.(row);
      onChange({ partyId: String(row.id), partyName: row.display_name });
      setCreateOpen(false);
      setComboOpen(false);
      setQ("");
      const kindLabel =
        newKind === "BROKER"
          ? "Broker"
          : newKind === "SUPPLIER"
            ? "Supplier"
            : newKind === "FARMER"
              ? "Farmer"
              : "Buyer";
      toast.success(`${kindLabel} added`);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-1">
      <Popover open={comboOpen} onOpenChange={setComboOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between font-normal gap-2"
            data-testid={testIdPrefix}
          >
            <span className="truncate flex-1 text-left font-normal text-foreground">
              {selectedLabel || <span className="text-muted-foreground">{placeholder}</span>}
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[280px]" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search…" value={q} onValueChange={setQ} />
            <CommandList>
              <CommandEmpty>
                {canCreate && q.trim() ? (
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent rounded-sm"
                    onClick={openCreate}
                  >
                    Add “{q.trim()}”
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground px-3 py-2 block">No matches.</span>
                )}
              </CommandEmpty>
              <CommandGroup>
                {filtered.map((p) => (
                  <CommandItem
                    key={p.id}
                    value={String(p.id)}
                    onSelect={() => {
                      onChange({ partyId: String(p.id), partyName: p.display_name });
                      setComboOpen(false);
                      setQ("");
                    }}
                  >
                    <span>{p.display_name}</span>
                    {p.kind ? <span className="ml-auto text-[10px] text-muted-foreground">{p.kind}</span> : null}
                  </CommandItem>
                ))}
              </CommandGroup>
              {canCreate ? (
                <div className="border-t p-2">
                  <Button type="button" variant="ghost" size="sm" className="w-full text-xs" onClick={openCreate}>
                    Add new{" "}
                    {createKind === "BROKER"
                      ? "broker"
                      : createKind === "SUPPLIER"
                        ? "supplier"
                        : createKind === "FARMER"
                          ? "farmer"
                          : "buyer"}
                    …
                  </Button>
                </div>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {partyId || partyName ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-xs h-7 px-0"
          onClick={() => onChange({ partyId: "", partyName: "" })}
        >
          Clear selection
        </Button>
      ) : null}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Add{" "}
              {createKind === "BROKER"
                ? "broker"
                : createKind === "SUPPLIER"
                  ? "supplier"
                  : createKind === "FARMER"
                    ? "farmer"
                    : "buyer"}
            </DialogTitle>
            <DialogDescription>
              Creates a party record you can reuse on future contracts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {createKind !== "BROKER" && createKind !== "SUPPLIER" && createKind !== "FARMER" ? (
              <div>
                <Label>Party type</Label>
                <Select value={newKind} onValueChange={setNewKind}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUYER_CREATE_KINDS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div>
              <Label>Name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button
              type="button"
              className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
              onClick={createParty}
              disabled={creating}
            >
              {creating ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
